'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import { useLang } from '@/lib/langContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface Props {
  onSwitchToRegister: () => void;
  onForgotPassword: () => void;
}

export default function LoginForm({ onSwitchToRegister, onForgotPassword }: Props) {
  const { t, dir } = useLang();
  const { signIn } = useAuth();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      await signIn(data.email, data.password, data.rememberMe);
      router.push('/dashboard');
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        setAuthError(
          dir === 'rtl' ? 'البريد الإلكتروني أو كلمة المرور غير صحيحة' : 'Invalid email or password'
        );
      } else {
        setAuthError(msg || (dir === 'rtl' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred, please try again'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="mb-7">
        <h2 className="text-[26px] font-800 text-foreground mb-1.5 leading-tight">
          {dir === 'rtl' ? 'مرحباً بعودتك 👋' : 'Welcome back 👋'}
        </h2>
        <p className="text-muted-foreground text-[14px]">
          {dir === 'rtl' ? 'أدخل بياناتك للوصول إلى لوحة التحكم' : 'Sign in to your account to continue'}
        </p>
      </div>

      {authError && (
        <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2.5 text-[13px] text-red-700">
          <Icon name="ExclamationCircleIcon" size={16} className="flex-shrink-0 mt-0.5" />
          <span>{authError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Email */}
        <div>
          <label className="block text-[13px] font-600 text-foreground mb-1.5">
            {t.email}
            <span className="text-status-expired ms-1">*</span>
          </label>
          <div className="relative">
            <div className="absolute start-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
              <Icon name="EnvelopeIcon" size={16} />
            </div>
            <input
              type="email"
              autoComplete="email"
              placeholder={dir === 'rtl' ? 'example@company.com' : 'you@company.com'}
              className={`input-base w-full ps-10 pe-4 py-2.5 text-[14px] ${errors.email ? 'border-status-expired' : ''}`}
              {...register('email', {
                required: t.required,
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t.invalidEmail },
              })}
            />
          </div>
          {errors.email && (
            <p className="mt-1.5 text-[12px] text-status-expired flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={13} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[13px] font-600 text-foreground">
              {t.password}
              <span className="text-status-expired ms-1">*</span>
            </label>
            <button type="button" className="text-[12px] text-primary font-500 hover:underline" onClick={onForgotPassword}>
              {t.forgotPassword}
            </button>
          </div>
          <div className="relative">
            <div className="absolute start-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
              <Icon name="LockClosedIcon" size={16} />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`input-base w-full ps-10 pe-12 py-2.5 text-[14px] ${errors.password ? 'border-status-expired' : ''}`}
              {...register('password', {
                required: t.required,
                minLength: { value: 8, message: t.passwordLength },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute end-3 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-[12px] text-status-expired flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={13} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember Me */}
        <label className="flex items-center gap-2.5 cursor-pointer group">
          <div className="relative">
            <input
              type="checkbox"
              className="sr-only peer"
              {...register('rememberMe')}
            />
            <div className="w-4 h-4 rounded border-2 border-border peer-checked:border-primary peer-checked:bg-primary transition-all flex items-center justify-center">
              <Icon name="CheckIcon" size={10} className="text-white opacity-0 peer-checked:opacity-100" />
            </div>
          </div>
          <span className="text-[13px] text-muted-foreground group-hover:text-foreground transition-colors">{t.rememberMe}</span>
        </label>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 rounded-xl text-[14px] font-600 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none mt-2"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              {t.loading}
            </>
          ) : (
            <>
              {t.login}
              <Icon name="ArrowRightIcon" size={15} />
            </>
          )}
        </button>
      </form>

      <div className="mt-6 pt-5 border-t border-border text-center">
        <p className="text-[13px] text-muted-foreground">
          {t.noAccount}{' '}
          <button onClick={onSwitchToRegister} className="text-primary font-600 hover:underline">
            {t.signUpNow}
          </button>
        </p>
      </div>
    </div>
  );
}